import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  PlusIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";

import {
  fetchOrgCharts,
  deleteOrgChart,
} from "../../../../api/quality/orgCharts";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../../components/utils/ResourceError";
import { ResourceEmpty } from "../../../../components/utils/ResourceEmpty";
import DashboardHeader from "../../../../components/dashboard/dashboardHeader";
import RoleOnly, { useUserHasAccess } from "../../../../routes/roleOnly";

export const OrgChartsList = () => {
  const { t } = useTranslation();
  const [orgCharts, setOrgCharts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const canCreate = useUserHasAccess({ role: "quality", action: "create" });
  const canUpdate = useUserHasAccess({ role: "quality", action: "update" });
  const canDelete = useUserHasAccess({ role: "quality", action: "destroy" });

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = {
        query: searchQuery,
        per_page: 50,
        page: 1,
      };
      const response = await fetchOrgCharts(params);
      setOrgCharts(response.data || []);
    } catch (err) {
      console.error("Error fetching org charts:", err);
      setError(true);
      toast.error(t("Kunne ikke laste organisasjonskart"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [searchQuery]);

  const handleDelete = async (id) => {
    if (
      window.confirm(
        t("Er du sikker på at du vil slette dette organisasjonskartet?")
      )
    ) {
      try {
        await deleteOrgChart(id);
        toast.success(t("Organisasjonskart ble slettet"));
        fetchData();
      } catch (err) {
        toast.error(t("Kunne ikke slette organisasjonskart"));
      }
    }
  };

  const canRead = useUserHasAccess({ role: "quality", action: "read" });

  if (loading && orgCharts.length === 0) {
    return <ResourceLoading size="base" />;
  }

  if (error) {
    return <ResourceError errors={[t("Kunne ikke laste organisasjonskart")]} />;
  }

  return (
    <>
      <DashboardHeader
        title={t("Organisasjonskart")}
        subtitle={t("Administrer organisasjonskart for bedriften")}
        mainButton={canCreate ? {
          label: t("Nytt organisasjonskart"),
          action: null,
          path: "/dashboard/hse-ks/org-charts/new",
          color: "",
          icon: PlusIcon,
        } : null}
      >
        <div className="mt-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Search */}
          <div className="mb-6">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder={t("Søk i organisasjonskart...")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>

          {/* List */}
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            {!orgCharts || orgCharts.length === 0 ? (
              <ResourceEmpty message={t("Ingen organisasjonskart funnet")} />
            ) : (
              <ul className="divide-y divide-gray-200">
                {orgCharts.map((orgChart) => (
                  <li key={orgChart.id} className="px-6 py-4 hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3 flex-1 min-w-0">
                        <div className="flex-shrink-0">
                          <DocumentTextIcon className="h-5 w-5 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {orgChart.title}
                            </p>
                            {orgChart.approved_revision && (
                              <CheckCircleIcon className="h-4 w-4 text-green-500" />
                            )}
                          </div>
                          {orgChart.description && (
                            <p className="text-sm text-gray-500 truncate">
                              {orgChart.description}
                            </p>
                          )}
                          <div className="flex items-center space-x-4 text-xs text-gray-500 mt-1">
                            {orgChart.approved_revision && (
                              <span className="flex items-center space-x-1">
                                <CheckCircleIcon className="h-3 w-3" />
                                <span>
                                  {t("Revisjon")}{" "}
                                  {orgChart.approved_revision.revision_number}
                                </span>
                              </span>
                            )}
                            <span>
                              {new Date(orgChart.created_at).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {(!orgChart?.approved_revision && canRead) ? (
                          <span
                            className="inline-flex items-center px-2.5 py-1.5 border border-gray-200 shadow-sm text-xs font-medium rounded text-gray-400 bg-gray-50 cursor-not-allowed"
                            aria-disabled="true"
                          >
                            <EyeIcon className="h-4 w-4 mr-1" />
                            {t("Vis")}
                          </span>
                        ) : (
                          <Link
                            to={`/dashboard/hse-ks/org-charts/${orgChart.id}`}
                            className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                          >
                            <EyeIcon className="h-4 w-4 mr-1" />
                            {t("Vis")}
                          </Link>
                        )}
                        <RoleOnly role="quality" action="update">
                          <Link
                            to={`/dashboard/hse-ks/org-charts/${orgChart.id}/edit`}
                            className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                          >
                            <PencilIcon className="h-4 w-4 mr-1" />
                            {t("Rediger")}
                          </Link>
                        </RoleOnly>
                        <RoleOnly role="quality" action="destroy">
                          <button
                            onClick={() => handleDelete(orgChart.id)}
                            className="inline-flex items-center px-2.5 py-1.5 border border-red-300 shadow-sm text-xs font-medium rounded text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                          >
                            <TrashIcon className="h-4 w-4 mr-1" />
                            {t("Slett")}
                          </button>
                        </RoleOnly>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </DashboardHeader>
    </>
  );
};

